/**
 * Coupon grants in WAF-safe chunks.
 *
 * WHY. `POST /v1/admin/coupons/grant` sits behind the PROD API WAF, whose CRS
 * rule `SizeRestrictions_BODY` blocks every request body over 8,192 bytes (WAF
 * plan item D5, docs/runbooks/2026-09-25-prod-waf-under-cdk.md). One user id costs
 * 39 bytes of JSON, so a single grant request for about 206 users or more got a
 * WAF 403 on PROD, and the admin saw only "Forbidden". D5 exempts only three
 * public POSTs from that rule on purpose; an admin route keeps the limit, so the
 * panel sends large grants in chunks instead.
 *
 * SAFE TO SEND AGAIN. The server skips a user who already holds a claim for the
 * promotion (`already_claimed`, a unique-key violation), so if a chunk fails, the
 * operator can send the same list again and only the missing users are granted.
 */

/** CRS SizeRestrictions_BODY on the PROD API WAF: a body over this many bytes is blocked. */
export const WAF_BODY_LIMIT_BYTES = 8192;

/** The largest grant body the panel sends: 1 KB under the WAF limit. */
export const GRANT_CHUNK_MAX_BYTES = 7 * 1024;

export interface GrantBody {
  promotion_id: string;
  user_ids: string[];
  reason: string;
  confirm_bulk?: 'GRANT';
}

const encoder = new TextEncoder();
export const bodyBytes = (body: GrantBody): number => encoder.encode(JSON.stringify(body)).length;

/**
 * Split one promotion's grant into bodies of at most `maxBytes` bytes (UTF-8),
 * keeping the user order and sending each id exactly once. `confirmBulk` is the
 * operator's confirmation for the WHOLE grant (more than 100 users); every chunk
 * carries it, because a chunk can itself hold more than 100 ids.
 * Throws when even one id does not fit — only an over-long reason can cause that.
 */
export function chunkGrantBodies(
  promotionId: string,
  userIds: readonly string[],
  reason: string,
  opts: { confirmBulk: boolean; maxBytes?: number },
): GrantBody[] {
  const maxBytes = opts.maxBytes ?? GRANT_CHUNK_MAX_BYTES;
  const make = (ids: string[]): GrantBody => ({
    promotion_id: promotionId,
    user_ids: ids,
    reason,
    ...(opts.confirmBulk ? { confirm_bulk: 'GRANT' as const } : {}),
  });
  const chunks: GrantBody[] = [];
  let current: string[] = [];
  for (const id of userIds) {
    const candidate = make([...current, id]);
    if (bodyBytes(candidate) <= maxBytes) {
      current.push(id);
      continue;
    }
    if (current.length === 0) {
      throw new Error(
        `The reason is too long to send (${encoder.encode(reason).length} bytes). Shorten it: each request must stay under ${maxBytes} bytes.`,
      );
    }
    chunks.push(make(current));
    current = [id];
    if (bodyBytes(make(current)) > maxBytes) {
      throw new Error(
        `The reason is too long to send (${encoder.encode(reason).length} bytes). Shorten it: each request must stay under ${maxBytes} bytes.`,
      );
    }
  }
  if (current.length > 0) chunks.push(make(current));
  return chunks;
}

export interface GrantSkip {
  user_id: string;
  reason: string;
}

export interface ChunkedGrantResult {
  /** Users granted, summed over the chunks that succeeded. */
  granted: number;
  /** Users the server skipped (already_claimed, cap_reached, …), over the chunks that succeeded. */
  skipped: GrantSkip[];
  /** Claim ids created, over the chunks that succeeded. */
  claim_ids: string[];
  chunksSent: number;
  chunksTotal: number;
  /** Set when a chunk failed; the chunks after it were NOT sent. */
  error?: string;
  /**
   * Users the server did not confirm: the failed chunk and every chunk after it.
   * Not "not sent": the failed chunk may have been sent (an HTTP 500 rolls it
   * back) or even committed (a network error after the server answered). A
   * re-send settles all three cases, because the server skips a user who
   * already has the coupon.
   */
  unconfirmed: number;
}

/**
 * Send the chunks one after another and stop at the first failure, so the
 * result says exactly which users were handled. The caller shows `error` with
 * the advice to send the same list again (already-granted users are skipped).
 */
export async function postGrantChunks(
  url: string,
  idToken: string | null,
  chunks: readonly GrantBody[],
  fetchImpl: typeof fetch = fetch,
): Promise<ChunkedGrantResult> {
  const out: ChunkedGrantResult = { granted: 0, skipped: [], claim_ids: [], chunksSent: 0, chunksTotal: chunks.length, unconfirmed: 0 };
  if (!idToken) {
    // No session: send nothing (the old code sent "Bearer null" and got a 401).
    out.error = 'Not signed in: sign in again, then send the grant';
    out.unconfirmed = chunks.reduce((s, c) => s + c.user_ids.length, 0);
    return out;
  }
  for (let i = 0; i < chunks.length; i++) {
    let failure: string | undefined;
    try {
      const r = await fetchImpl(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify(chunks[i]),
      });
      if (r.ok) {
        const result = (await r.json()) as { granted: number | unknown[]; skipped?: GrantSkip[]; claim_ids?: string[] };
        out.granted += Array.isArray(result.granted) ? result.granted.length : Number(result.granted) || 0;
        out.skipped.push(...(result.skipped ?? []));
        out.claim_ids.push(...(result.claim_ids ?? []));
        out.chunksSent++;
        continue;
      }
      // API errors are RFC 9457 Problem Details (detail/title); the WAF's own 403 body is {"message":"Forbidden"}.
      const err = (await r.json().catch(() => ({}))) as { detail?: string; title?: string; message?: string };
      const text = err.detail ?? err.title ?? err.message;
      failure = text ? `${text} (HTTP ${r.status})` : `HTTP ${r.status}`;
    } catch (e: unknown) {
      failure = e instanceof Error ? e.message : String(e);
    }
    out.error = `Request ${i + 1} of ${chunks.length} failed: ${failure}`;
    out.unconfirmed = chunks.slice(i).reduce((s, c) => s + c.user_ids.length, 0);
    return out;
  }
  return out;
}
